/**
 * CommunityHub - Landing page for Community & Local Life
 * Shows all 8 categories with featured posts, active groups, and upcoming classes
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Users, GraduationCap, Search, HeartHandshake, Gift, 
  Megaphone, Car, HelpingHand, ArrowRight, MapPin, 
  Calendar, Clock, Star, TrendingUp, Sparkles, Plus,
  ChevronRight, Loader2, Heart, Eye, Phone
} from 'lucide-react';
import { useCountry } from '../context/CountryContext';
import CommunityContactModal from '../components/community/CommunityContactModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category configuration
const CATEGORIES = [
  { 
    slug: 'ride-sharing', 
    name: 'Ride Sharing', 
    icon: Car, 
    color: 'bg-green-500',
    gradient: 'from-green-500 to-emerald-600',
    description: 'Share rides, save money, reduce traffic'
  },
  { 
    slug: 'classes-workshops', 
    name: 'Classes & Workshops', 
    icon: GraduationCap, 
    color: 'bg-purple-500',
    gradient: 'from-purple-500 to-pink-600',
    description: 'Learn new skills from local instructors'
  },
  { 
    slug: 'lost-found', 
    name: 'Lost & Found', 
    icon: Search, 
    color: 'bg-amber-500',
    gradient: 'from-amber-500 to-orange-600',
    description: 'Help reunite lost items with owners'
  },
  { 
    slug: 'volunteers', 
    name: 'Volunteers', 
    icon: HeartHandshake, 
    color: 'bg-rose-500',
    gradient: 'from-rose-500 to-red-600',
    description: 'Give back to your community'
  },
  { 
    slug: 'charity-donations', 
    name: 'Charity & Donations', 
    icon: Gift, 
    color: 'bg-emerald-500',
    gradient: 'from-emerald-500 to-teal-600',
    description: 'Donate or find items in need'
  },
  { 
    slug: 'local-groups-clubs', 
    name: 'Local Groups & Clubs', 
    icon: Users, 
    color: 'bg-blue-500',
    gradient: 'from-blue-500 to-indigo-600',
    description: 'Connect with like-minded people'
  },
  { 
    slug: 'notices-announcements', 
    name: 'Notices & Announcements', 
    icon: Megaphone, 
    color: 'bg-cyan-500',
    gradient: 'from-cyan-500 to-blue-600',
    description: 'Stay informed about local news'
  },
  { 
    slug: 'community-help', 
    name: 'Community Help', 
    icon: HelpingHand, 
    color: 'bg-violet-500',
    gradient: 'from-violet-500 to-purple-600',
    description: 'Ask for or offer help to neighbors'
  }
];

// Category Card Component
const CategoryCard = ({ category, postCount, onClick }) => {
  const Icon = category.icon;
  
  return (
    <Card 
      className="group cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-2 hover:border-blue-300"
      onClick={onClick}
      data-testid={`hub-category-${category.slug}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-xl bg-gradient-to-br ${category.gradient} shadow-lg`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-lg group-hover:text-blue-600 transition-colors">
              {category.name}
            </h3>
            <p className="text-sm text-gray-500 mt-1">{category.description}</p>
            <div className="flex items-center justify-between mt-3">
              <Badge variant="outline" className="text-xs">
                {postCount} active posts
              </Badge>
              <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Featured Post Card
const FeaturedPostCard = ({ post, country }) => {
  const navigate = useNavigate();
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';
  
  // Find category config
  const categoryConfig = CATEGORIES.find(c => c.slug === post.category) || CATEGORIES[0];
  const Icon = categoryConfig.icon;
  
  return (
    <Card className="group hover:shadow-lg transition-all overflow-hidden">
      <div className="relative h-40">
        {post.images?.[0] ? (
          <img 
            src={post.images[0]} 
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${categoryConfig.gradient} flex items-center justify-center`}>
            <Icon className="w-12 h-12 text-white/50" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <Badge className={`absolute top-2 left-2 ${categoryConfig.color} text-white`}>
          {categoryConfig.name}
        </Badge>
        {post.featured && (
          <Badge className="absolute top-2 right-2 bg-yellow-500 text-white">
            <Star className="w-3 h-3 mr-1" />
            Featured
          </Badge>
        )}
      </div>
      <CardContent className="p-4">
        <h4 className="font-bold line-clamp-1 group-hover:text-blue-600">{post.title}</h4>
        <p className="text-sm text-gray-500 line-clamp-2 mt-1">{post.description}</p>
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <MapPin className="w-3 h-3" />
            {post.location}
          </div>
          {post.price > 0 && (
            <span className="font-bold text-blue-600">
              {currencySymbol}{post.price?.toLocaleString()}
            </span>
          )}
        </div>
        <div className="flex gap-2 mt-3">
          <Button 
            size="sm" 
            className="flex-1"
            onClick={() => navigate(`/listing/${post.id}`)}
          >
            View Details
          </Button>
          <CommunityContactModal 
            post={post} 
            country={country}
            trigger={
              <Button size="sm" variant="outline">
                <Phone className="w-4 h-4" />
              </Button>
            }
          />
        </div>
      </CardContent>
    </Card>
  );
};

// Upcoming Class Card
const UpcomingClassCard = ({ classItem, country }) => {
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';
  
  return (
    <Card className="hover:shadow-md transition-all">
      <CardContent className="p-4">
        <div className="flex gap-4">
          <div className="w-16 h-16 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
            <GraduationCap className="w-8 h-8 text-purple-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold line-clamp-1">{classItem.title}</h4>
            <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
              <MapPin className="w-3 h-3" />
              {classItem.location}
            </div>
            <div className="flex items-center justify-between mt-2">
              <Badge variant="outline" className="text-xs">
                {classItem.extra_fields?.skill_level || 'All levels'}
              </Badge>
              <span className="font-bold text-purple-600 text-sm">
                {currencySymbol}{classItem.extra_fields?.price_per_session?.toLocaleString() || '0'}/session
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Stats Component
const CommunityStats = ({ stats }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
    <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
      <CardContent className="p-4 text-center">
        <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-80" />
        <p className="text-3xl font-bold">{stats.totalPosts}</p>
        <p className="text-sm opacity-80">Active Posts</p>
      </CardContent>
    </Card>
    <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
      <CardContent className="p-4 text-center">
        <Users className="w-8 h-8 mx-auto mb-2 opacity-80" />
        <p className="text-3xl font-bold">{stats.totalProviders}</p>
        <p className="text-sm opacity-80">Service Providers</p>
      </CardContent>
    </Card>
    <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
      <CardContent className="p-4 text-center">
        <GraduationCap className="w-8 h-8 mx-auto mb-2 opacity-80" />
        <p className="text-3xl font-bold">{stats.upcomingClasses}</p>
        <p className="text-sm opacity-80">Classes Available</p>
      </CardContent>
    </Card>
    <Card className="bg-gradient-to-br from-rose-500 to-rose-600 text-white">
      <CardContent className="p-4 text-center">
        <HeartHandshake className="w-8 h-8 mx-auto mb-2 opacity-80" />
        <p className="text-3xl font-bold">{stats.volunteerOpps}</p>
        <p className="text-sm opacity-80">Volunteer Opps</p>
      </CardContent>
    </Card>
  </div>
);

export default function CommunityHub() {
  const navigate = useNavigate();
  const { country } = useCountry();
  const [loading, setLoading] = useState(true);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [stats, setStats] = useState({
    totalPosts: 0,
    totalProviders: 0,
    upcomingClasses: 0,
    volunteerOpps: 0
  });
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch featured posts (listings from community categories)
        const postsPromises = CATEGORIES.map(cat => 
          fetch(`${API_URL}/api/listings/?category=${cat.slug}&limit=3&country_code=${country}`)
            .then(r => r.json())
            .catch(() => ({ listings: [], total: 0 }))
        );
        
        const postsResults = await Promise.all(postsPromises);
        
        // Build category counts
        const counts = {};
        CATEGORIES.forEach((cat, idx) => {
          counts[cat.slug] = postsResults[idx]?.total || 0;
        });
        setCategoryCounts(counts);
        
        // Get featured posts (first 2 from each category that has featured)
        const featured = [];
        postsResults.forEach((result, idx) => {
          const categoryPosts = result?.listings || [];
          const featuredFromCat = categoryPosts.filter(p => p.featured).slice(0, 1);
          if (featuredFromCat.length > 0) {
            featured.push(...featuredFromCat.map(p => ({...p, category: CATEGORIES[idx].slug})));
          } else if (categoryPosts.length > 0) {
            featured.push({...categoryPosts[0], category: CATEGORIES[idx].slug});
          }
        });
        setFeaturedPosts(featured.slice(0, 8));
        
        // Fetch community posts for classes
        const classesResponse = await fetch(`${API_URL}/api/community/posts?category=classes-workshops&status=active&limit=4&country=${country}`);
        const classesData = await classesResponse.json();
        setUpcomingClasses(classesData?.posts || []);
        
        // Fetch providers count
        const providersResponse = await fetch(`${API_URL}/api/community/providers?country=${country}&limit=1`);
        const providersData = await providersResponse.json();
        
        // Fetch volunteer posts count
        const volunteersResponse = await fetch(`${API_URL}/api/listings/?category=volunteers&limit=1&country_code=${country}`);
        const volunteersData = await volunteersResponse.json();
        
        // Calculate stats
        const totalPosts = Object.values(counts).reduce((a, b) => a + b, 0);
        setStats({
          totalPosts,
          totalProviders: providersData?.total || 13,
          upcomingClasses: classesData?.total || 0,
          volunteerOpps: volunteersData?.total || 0
        });
        
      } catch (error) {
        console.error('Error fetching community data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [country]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-500">Loading community...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex-1">
              <Badge className="bg-white/20 text-white mb-4">
                <Sparkles className="w-3 h-3 mr-1" />
                Community & Local Life
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Your Local Community Hub
              </h1>
              <p className="text-xl text-blue-100 mb-6 max-w-xl">
                Connect with neighbors, share rides, learn new skills, volunteer, and more. 
                Building stronger communities together.
              </p>
              <div className="flex gap-3">
                <Button 
                  size="lg" 
                  className="bg-white text-blue-700 hover:bg-blue-50"
                  onClick={() => navigate('/community/post')}
                  data-testid="hub-post-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Create a Post
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white/30 text-white hover:bg-white/10"
                  onClick={() => document.getElementById('categories').scrollIntoView({ behavior: 'smooth' })}
                >
                  Explore Categories
                </Button>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="grid grid-cols-2 gap-3">
                {CATEGORIES.slice(0, 4).map(cat => {
                  const Icon = cat.icon;
                  return (
                    <div 
                      key={cat.slug}
                      className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center hover:bg-white/20 transition-colors cursor-pointer"
                      onClick={() => navigate(`/category/${cat.slug}`)}
                    >
                      <Icon className="w-8 h-8 mx-auto mb-2" />
                      <p className="text-sm font-medium">{cat.name}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <CommunityStats stats={stats} />
        
        {/* Categories Grid */}
        <div id="categories" className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold">Browse Categories</h2>
              <p className="text-gray-500">Find what you're looking for</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {CATEGORIES.map(category => (
              <CategoryCard 
                key={category.slug}
                category={category}
                postCount={categoryCounts[category.slug] || 0}
                onClick={() => navigate(`/category/${category.slug}`)}
              />
            ))}
          </div>
        </div>
        
        {/* Featured Posts */}
        {featuredPosts.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold">Featured Posts</h2>
                <p className="text-gray-500">Popular listings from the community</p>
              </div>
              <Button variant="outline" onClick={() => navigate('/category/community-local-life')}>
                View All
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {featuredPosts.slice(0, 4).map(post => (
                <FeaturedPostCard key={post.id} post={post} country={country} />
              ))}
            </div>
          </div>
        )}
        
        {/* Two Column: Classes & Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Upcoming Classes */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <GraduationCap className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <CardTitle>Classes & Workshops</CardTitle>
                      <p className="text-sm text-gray-500">Learn something new</p>
                    </div>
                  </div>
                  <Button variant="link" onClick={() => navigate('/category/classes-workshops')}>
                    See all
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {upcomingClasses.length > 0 ? (
                  <div className="space-y-3">
                    {upcomingClasses.map(classItem => (
                      <UpcomingClassCard key={classItem.id} classItem={classItem} country={country} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No classes available yet</p>
                    <Button 
                      variant="link" 
                      onClick={() => navigate('/community/post?category=classes-workshops')}
                    >
                      Post a class
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Quick Actions */}
          <div>
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  className="w-full justify-start bg-green-600 hover:bg-green-700"
                  onClick={() => navigate('/community/post?category=ride-sharing')}
                >
                  <Car className="w-4 h-4 mr-2" />
                  Offer a Ride
                </Button>
                <Button 
                  className="w-full justify-start bg-purple-600 hover:bg-purple-700"
                  onClick={() => navigate('/community/post?category=classes-workshops')}
                >
                  <GraduationCap className="w-4 h-4 mr-2" />
                  Post a Class
                </Button>
                <Button 
                  className="w-full justify-start bg-amber-600 hover:bg-amber-700"
                  onClick={() => navigate('/community/post?category=lost-found')}
                >
                  <Search className="w-4 h-4 mr-2" />
                  Report Lost/Found
                </Button>
                <Button 
                  className="w-full justify-start bg-rose-600 hover:bg-rose-700"
                  onClick={() => navigate('/community/post?category=volunteers')}
                >
                  <HeartHandshake className="w-4 h-4 mr-2" />
                  Volunteer
                </Button>
                <Button 
                  className="w-full justify-start bg-violet-600 hover:bg-violet-700"
                  onClick={() => navigate('/community/post?category=community-help')}
                >
                  <HelpingHand className="w-4 h-4 mr-2" />
                  Ask for Help
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Bottom CTA */}
        <Card className="mt-12 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold mb-2">Ready to Connect?</h3>
            <p className="text-blue-100 mb-6 max-w-xl mx-auto">
              Join thousands of neighbors building a stronger community. Post your first listing today!
            </p>
            <Button 
              size="lg" 
              className="bg-white text-blue-700 hover:bg-blue-50"
              onClick={() => navigate('/community/post')}
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Your First Post
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
